import {inject, Pipe, PipeTransform} from '@angular/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';

/**
 * Le modèle répond en markdown : listes, gras, italique. Sans rendu, l'utilisateur lit les astérisques.
 * Seul un sous-ensemble est reconnu — c'est tout ce que produit l'assistant.
 *
 * Le texte est échappé AVANT toute génération : les seules balises qui sortent d'ici sont les nôtres.
 * La réponse peut reprendre une description de tâche saisie par n'importe qui, donc du HTML hostile.
 */
@Pipe({name: 'aiMarkdown', pure: true})
export class AiMarkdownPipe implements PipeTransform {
  private readonly sanitizer = inject(DomSanitizer);

  transform(text: string | null | undefined): SafeHtml {
    if (!text) return '';

    const blocks = AiMarkdownPipe.splitIntoBlocks(AiMarkdownPipe.escape(text));

    return this.sanitizer.bypassSecurityTrustHtml(blocks.map(AiMarkdownPipe.renderBlock).join(''));
  }

  private static escape(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  /** Un bloc = des lignes consécutives non vides. Une ligne vide sépare deux blocs. */
  private static splitIntoBlocks(text: string): string[][] {
    const blocks: string[][] = [];
    let current: string[] = [];

    for (const line of text.split('\n')) {
      const trimmed = line.trim();

      if (trimmed) {
        current.push(trimmed);
        continue;
      }

      if (current.length > 0) blocks.push(current);
      current = [];
    }

    if (current.length > 0) blocks.push(current);

    return blocks;
  }

  private static renderBlock(lines: string[]): string {
    const bullets = lines.filter(line => /^[-*]\s+/.test(line));
    if (bullets.length === lines.length) return AiMarkdownPipe.renderList(lines, 'ul', /^[-*]\s+/);

    const numbered = lines.filter(line => /^\d+[.)]\s+/.test(line));
    if (numbered.length === lines.length) return AiMarkdownPipe.renderList(lines, 'ol', /^\d+[.)]\s+/);

    return `<p class="mb-2 last:mb-0">${lines.map(AiMarkdownPipe.renderInline).join('<br>')}</p>`;
  }

  private static renderList(lines: string[], tag: 'ul' | 'ol', marker: RegExp): string {
    const items = lines
      .map(line => `<li>${AiMarkdownPipe.renderInline(line.replace(marker, ''))}</li>`)
      .join('');

    // Les listes sont remises à zéro par le reset Tailwind : les puces sont rendues ici.
    const style = tag === 'ul' ? 'list-disc' : 'list-decimal';

    return `<${tag} class="${style} mb-2 pl-5 last:mb-0">${items}</${tag}>`;
  }

  private static renderInline(text: string): string {
    return text
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>')
      .replace(/`([^`]+)`/g, '<code class="rounded bg-[var(--p-surface-200)] px-1">$1</code>');
  }
}
